// LureoButton's look by variant, shared with links that must look like the button (src/common/LureoButton.tsx). A variant's wash
// (its before layer) sits behind the label, which is often bare text: a set's hover can be a solid fill.
import { FOCUS } from '@/components/ui/recipes';

export type LureoButtonVariant = 'primary' | 'secondary' | 'solid' | 'menu' | 'outline-ink-strong' | 'outline-3' | 'outline-2' | 'no-outline' | 'danger';

/** A look only a link takes: an app store's download, or a second choice on a deep fill (the share pages). */
type LinkVariant = 'store' | 'on-emphasis';

const squash = (classes: string) => classes.replace(/\s+/g, ' ').trim();

/** LureoButton's look for a variant, for an element that must be something else (a link) but look like the button. */
export function lureoButtonClass(variant: LureoButtonVariant | LinkVariant = 'primary', isSelected = false): string {
  const baseClasses = `
    relative isolate inline-flex items-center gap-2.5 rounded-lureo-radius-control before:-z-10
    text-center font-lureo-font-ui text-lureo-16 font-lureo-weight-button leading-snug text-lureo-button-ink case-lureo-label-case font-style-lureo-label-style text-shadow-lureo-label-shadow tracking-lureo-label-tracking
    cursor-pointer
    transition-all duration-lureo-duration-button ease-lureo-easing-standard
    ${FOCUS}
    disabled:cursor-not-allowed disabled:opacity-lureo-disabled-opacity
    hover:hover-move hover:scale-lureo-hover-scale
    active:scale-lureo-press-scale active:translate-y-lureo-press-offset
    disabled-fill
  `;

  // Filled buttons cast the set's button shadow; outlined buttons, the call to action and the danger button each cast their own, at rest, under the pointer and pressed
  const pressed = 'active:shadow-lureo-shadow-button-pressed';
  const shadow = `shadow-lureo-shadow-button hover:shadow-lureo-shadow-button-hover ${pressed}`;
  const outlineShadow = 'shadow-lureo-shadow-outline-button hover:shadow-lureo-shadow-outline-button-hover active:shadow-lureo-shadow-outline-button-pressed';

  const variantClasses = {
    primary: `
      ${shadow}
      bg-linear-to-b from-lureo-cta-soft-from to-lureo-cta-soft-to text-lureo-on-cta-soft
      rounded-lureo-radius-control-lg
      ring-[1px] ring-lureo-button-ring ring-inset
      before:absolute before:inset-0 before:rounded-[inherit] before:opacity-0
      before:bg-lureo-cta-soft-hover before:transition-opacity
      hover:before:opacity-100 active:before:bg-lureo-cta-soft-pressed active:before:opacity-100
      hover:text-lureo-on-cta-hover active:text-lureo-on-cta-pressed
      [&>*]:relative [&>*]:z-10
    `,
    solid: `
      shadow-lureo-shadow-solid hover:shadow-lureo-shadow-solid-hover active:shadow-lureo-shadow-solid-pressed
      bg-lureo-secondary text-lureo-on-secondary
      rounded-lureo-radius-control-lg
      ring-[1px] ring-lureo-button-ring ring-inset
      before:absolute before:inset-0 before:rounded-[inherit] before:opacity-0
      before:bg-lureo-secondary-hover before:transition-opacity
      hover:before:opacity-100 active:before:bg-lureo-secondary-pressed active:before:opacity-100
      hover:text-lureo-on-secondary-hover active:text-lureo-on-secondary-pressed
      [&>*]:relative [&>*]:z-10
    `,
    // The call to action
    secondary: `
      shadow-lureo-shadow-cta hover:shadow-lureo-shadow-cta-hover active:shadow-lureo-shadow-cta-pressed
      text-lureo-on-cta cta-fill
      ring-[1px] ring-lureo-ink-strong/5 ring-inset
      before:absolute before:inset-0 before:rounded-[inherit] before:bg-lureo-cta-hover before:opacity-0
      before:transition-opacity
      hover:before:opacity-100 active:before:bg-lureo-cta-pressed active:before:opacity-100
      hover:text-lureo-cta-label-hover active:text-lureo-cta-label-pressed
      [&>*]:relative [&>*]:z-10
    `,
    // The selected state comes last: merging keeps the last of two conflicting classes
    menu: `
      ${pressed}
      bg-transparent border-lureo-border-width-medium
      before:absolute before:inset-0 before:rounded-[inherit] before:opacity-0
      before:bg-lureo-brand-ink before:transition-opacity
      active:before:opacity-20 active:text-lureo-brand-ink
      [&>*]:relative [&>*]:z-10
      transition-all duration-lureo-duration-button
      ${isSelected ? 'border-lureo-brand-ink text-lureo-brand-ink before:opacity-10' : 'text-lureo-ink border-transparent hover:before:opacity-10 hover:text-lureo-brand-ink hover:border-lureo-brand-ink/10'}
    `,
    'outline-ink-strong': `
      ${outlineShadow}
      bg-transparent
      ring-[1px] ring-lureo-line-strong/60 ring-inset
      hover:bg-linear-to-b hover:from-lureo-cta-soft-from hover:to-lureo-cta-soft-to
      hover:text-lureo-on-cta-soft active:text-lureo-on-cta-soft
      hover:ring-[1px] hover:ring-lureo-line-strong/20 active:ring-[1px] active:ring-lureo-line-strong/50
      transition-all duration-lureo-duration-button
    `,
    'outline-2': `
      ${outlineShadow}
      bg-transparent
      border-lureo-border-width border-lureo-line-strong
      before:absolute before:inset-0 before:rounded-[inherit] before:opacity-0
      before:bg-lureo-outline-hover before:transition-opacity
      hover:before:opacity-100 active:before:bg-lureo-outline-pressed active:before:opacity-100
      hover:text-lureo-on-outline-hover active:text-lureo-on-outline-hover
      hover:border-lureo-outline-line-hover active:border-lureo-outline-line-hover
      [&>*]:relative [&>*]:z-10
      transition-all duration-lureo-duration-button
    `,
    'outline-3': `
      ${outlineShadow}
      bg-transparent
      border-lureo-border-width-medium border-lureo-brand-ink text-lureo-brand-ink
      before:absolute before:inset-0 before:rounded-[inherit] before:opacity-0
      before:bg-lureo-brand-ink before:transition-opacity
      hover:before:opacity-10 active:before:opacity-20
      [&>*]:relative [&>*]:z-10
      transition-all duration-lureo-duration-button
    `,
    'no-outline': `
      ${pressed}
      bg-transparent
      before:absolute before:inset-0 before:rounded-[inherit] before:opacity-0
      before:bg-lureo-hover before:transition-opacity
      hover:before:opacity-100 active:before:opacity-100
      active:text-lureo-brand-ink-pressed
      [&>*]:relative [&>*]:z-10
      transition-all duration-lureo-duration-button
    `,
    // Deleting or signing out: the set's danger colour, with its own hover and pressed shades
    danger: `
      shadow-lureo-shadow-danger hover:shadow-lureo-shadow-danger-hover active:shadow-lureo-shadow-danger-pressed
      bg-lureo-danger text-lureo-on-emphasis
      ring-[1px] ring-lureo-button-ring ring-inset
      hover:bg-lureo-danger-hover hover:text-lureo-on-emphasis
      active:bg-lureo-danger-pressed active:text-lureo-on-emphasis
    `,
    // An app store's download: black with white words in every theme
    store: `${pressed} bg-lureo-shade text-lureo-highlight hover:bg-lureo-shade/80 active:bg-lureo-shade/70`,
    // A second choice beside the call to action on a deep fill: see-through, ringed in the text on it
    'on-emphasis': `${pressed} bg-lureo-on-emphasis/12 ring-[1px] ring-inset ring-lureo-on-emphasis/35 text-lureo-on-emphasis hover:bg-lureo-on-emphasis/20 active:bg-lureo-on-emphasis/25`,
  };

  return `${squash(baseClasses)} ${squash(variantClasses[variant])}`;
}
